import { Link } from '@inertiajs/react'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { useTheme } from '../../hooks/useTheme'
import { SANDBOX_PRESET_TAG_LIST } from '../../lib/sandbox/presetTags'
import type { SandboxPresetTag } from '../../lib/sandbox/presetTags'
import { buildPreviewSrcDoc } from '../../lib/sandboxPreview'
import {
  absoluteSandboxUrlWithPreset,
  sandboxHrefWithPreset,
  type SandboxComponentPreset,
} from '../../lib/sandboxComponentPresets'

/** Square top so the strip meets the preview; bottom radius matches the card (`rounded-xl`). */
const LOAD_ROW_CLASS =
  'flex w-full items-center justify-center gap-2 rounded-b-xl rounded-t-none bg-primary-container px-3 py-2 font-headline text-xs font-bold tracking-wide text-on-primary shadow-[0_4px_0_0_#9a4000] transition-all enabled:active:translate-y-0.5 enabled:active:shadow-none'

const COPY_ROW_CLASS =
  'flex w-full items-center justify-center gap-2 rounded-lg border border-outline-variant/35 bg-surface-container-high/80 px-3 py-2 font-headline text-xs font-bold tracking-wide text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-on-surface'

const COPY_ROW_COPIED_CLASS =
  ' border-primary-container/50 bg-primary-container/12 text-primary-container hover:bg-primary-container/18 hover:text-primary-container'

const COPY_ROW_ERR_CLASS =
  ' border-error/40 bg-error/10 text-error hover:bg-error/15 hover:text-error'

const TAG_CHIP_CLASS =
  'rounded-md border px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-wide transition-colors'

type Props = {
  presets: SandboxComponentPreset[]
  strictMode: boolean
  onLoad: (preset: SandboxComponentPreset) => void
  /**
   * `drawer` — load into the current sandbox (slide-out panel).
   * `page` — primary action opens `/tools/sandbox?preset=…` in a new tab.
   */
  variant?: 'drawer' | 'page'
  /** When the drawer opens, focus moves to the search field (drawer / modal pattern). */
  drawerOpen?: boolean
}

/** Strip tags for search so preview copy (e.g. “Hello”) matches; script bodies are omitted. */
function presetSearchHaystack(preset: SandboxComponentPreset): string {
  const fromHtml = preset.html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
  return [
    preset.id.replace(/-/g, ' '),
    preset.title,
    preset.description,
    preset.category ?? '',
    ...preset.tags,
    fromHtml,
  ]
    .join(' ')
    .toLowerCase()
}

function presetMatchesQuery(preset: SandboxComponentPreset, raw: string): boolean {
  const q = raw.trim().toLowerCase()
  if (!q) return true
  return presetSearchHaystack(preset).includes(q)
}

function presetMatchesSelectedTags(preset: SandboxComponentPreset, selected: SandboxPresetTag[]): boolean {
  if (selected.length === 0) return true
  return selected.some((s) => preset.tags.includes(s))
}

function PresetThumb({ srcDoc }: { srcDoc: string }) {
  return (
    <div className="relative h-40 w-full shrink-0 overflow-hidden rounded-t-xl border border-outline-variant/20 bg-surface-container-high/25">
      <div className="pointer-events-none absolute inset-0 origin-top-left scale-[0.52] [height:192%] [width:192%]">
        <iframe
          title="Component preview"
          sandbox="allow-scripts"
          referrerPolicy="no-referrer"
          className="size-full max-h-none min-h-0 min-w-0 border-0 bg-surface-container-low"
          srcDoc={srcDoc}
          loading="lazy"
        />
      </div>
    </div>
  )
}

function GridList({
  filtered,
  variant,
  copyFlash,
  setCopyFlash,
  onLoad,
}: {
  filtered: { preset: SandboxComponentPreset; srcDoc: string }[]
  variant: 'drawer' | 'page'
  copyFlash: { presetId: string; ok: boolean } | null
  setCopyFlash: (v: { presetId: string; ok: boolean } | null) => void
  onLoad: (preset: SandboxComponentPreset) => void
}) {
  return (
    <ul className="m-0 grid list-none grid-cols-1 content-start items-stretch gap-4 bg-surface-container-low p-4 sm:grid-cols-2 xl:grid-cols-3">
      {filtered.length === 0 ? (
        <li className="col-span-full block rounded-lg border border-outline-variant/15 bg-surface-container-lowest/60 px-4 py-8 text-center font-mono text-xs text-on-surface-variant">
          No examples match your filters. Try a different search, tag combination, or clear filters.
        </li>
      ) : (
        filtered.map(({ preset, srcDoc }) => (
          <li key={preset.id} className="flex h-full min-h-0 min-w-0 flex-col">
            <article className="flex min-h-0 min-w-0 flex-1 flex-col gap-2 overflow-hidden rounded-xl border border-outline-variant/25 bg-surface-container-lowest px-4 py-3 shadow-[0_2px_8px_-2px_rgb(0_0_0/_0.35)]">
              <PresetThumb srcDoc={srcDoc} />
              <div className="flex min-w-0 flex-col gap-2.5">
                <div className="flex flex-wrap items-baseline gap-2">
                  <h3 className="shrink-0 font-headline text-sm font-semibold leading-snug text-on-surface">
                    {preset.title}
                  </h3>
                  {preset.category ? (
                    <span className="rounded border border-outline-variant/25 bg-surface-container-high/60 px-1.5 py-px font-mono text-[9px] tracking-wide text-on-surface-variant uppercase">
                      {preset.category}
                    </span>
                  ) : null}
                </div>
                <p className="line-clamp-3 font-mono text-[10px] leading-relaxed text-on-surface-variant">
                  {preset.description}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {preset.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-md border border-outline-variant/20 bg-surface-container-high/80 px-1.5 py-0.5 font-mono text-[9px] tracking-wide text-on-surface-variant uppercase"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              {variant === 'page' ? (
                <div className="mt-auto flex shrink-0 flex-col gap-2">
                  <a
                    href={sandboxHrefWithPreset(preset.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${LOAD_ROW_CLASS} no-underline hover:text-on-primary`}
                    title={`Open ${preset.title} in the sandbox (new tab)`}
                    aria-label={`Open ${preset.title} in the sandbox in a new tab`}
                  >
                    <span className="material-symbols-outlined text-[22px]">open_in_new</span>
                    Open in sandbox
                  </a>
                  <button
                    type="button"
                    className={`${COPY_ROW_CLASS}${
                      copyFlash?.presetId === preset.id
                        ? copyFlash.ok
                          ? COPY_ROW_COPIED_CLASS
                          : COPY_ROW_ERR_CLASS
                        : ''
                    }`}
                    aria-label={
                      copyFlash?.presetId === preset.id
                        ? copyFlash.ok
                          ? 'URL copied to clipboard'
                          : 'Copy failed'
                        : `Copy full sandbox URL for ${preset.title}`
                    }
                    title="Copy full URL (with origin)"
                    onClick={() => {
                      void (async () => {
                        try {
                          await navigator.clipboard.writeText(absoluteSandboxUrlWithPreset(preset.id))
                          setCopyFlash({ presetId: preset.id, ok: true })
                        } catch {
                          setCopyFlash({ presetId: preset.id, ok: false })
                        }
                      })()
                    }}
                  >
                    <span className="material-symbols-outlined text-[18px]" aria-hidden>
                      {copyFlash?.presetId === preset.id
                        ? copyFlash.ok
                          ? 'check'
                          : 'error'
                        : 'link'}
                    </span>
                    {copyFlash?.presetId === preset.id
                      ? copyFlash.ok
                        ? 'Copied!'
                        : "Couldn't copy"
                      : 'Copy link'}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className={`${LOAD_ROW_CLASS} mt-auto shrink-0`}
                  title={`Load ${preset.title}`}
                  aria-label={`Load ${preset.title} into sandbox`}
                  onClick={() => onLoad(preset)}
                >
                  <span className="material-symbols-outlined text-[22px]">play_arrow</span>
                  Load into sandbox
                </button>
              )}
            </article>
          </li>
        ))
      )}
    </ul>
  )
}

export default function ComponentLibraryPanel({
  presets,
  strictMode,
  onLoad,
  variant = 'drawer',
  drawerOpen = false,
}: Props) {
  const { theme: previewTheme } = useTheme()
  const [query, setQuery] = useState('')
  const [activeTags, setActiveTags] = useState<SandboxPresetTag[]>([])
  /** Small viewports: tag chips sit behind a disclosure; md+ always shows the chip grid. */
  const [tagsExpanded, setTagsExpanded] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)
  const embeddedInDrawer = variant === 'drawer'

  const [copyFlash, setCopyFlash] = useState<{ presetId: string; ok: boolean } | null>(null)

  useEffect(() => {
    if (copyFlash === null) return
    const ms = copyFlash.ok ? 2000 : 3200
    const t = window.setTimeout(() => setCopyFlash(null), ms)
    return () => window.clearTimeout(t)
  }, [copyFlash])

  useLayoutEffect(() => {
    if (!embeddedInDrawer || !drawerOpen) return
    searchRef.current?.focus()
  }, [embeddedInDrawer, drawerOpen])

  const allTags = useMemo(() => {
    const used = new Set<SandboxPresetTag>()
    for (const p of presets) {
      for (const t of p.tags) used.add(t)
    }
    return SANDBOX_PRESET_TAG_LIST.filter((t) => used.has(t))
  }, [presets])

  const thumbs = useMemo(
    () =>
      presets.map((p) => {
        const scriptBody = strictMode ? `'use strict';\n${p.js}` : p.js
        return {
          preset: p,
          srcDoc: buildPreviewSrcDoc(p.html, p.css, scriptBody, { theme: previewTheme }),
        }
      }),
    [presets, previewTheme, strictMode],
  )

  const filtered = useMemo(
    () =>
      thumbs.filter(
        ({ preset }) =>
          presetMatchesQuery(preset, query) && presetMatchesSelectedTags(preset, activeTags),
      ),
    [thumbs, query, activeTags],
  )

  function toggleTag(tag: SandboxPresetTag) {
    setActiveTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]))
  }

  function clearFilters() {
    setQuery('')
    setActiveTags([])
  }

  const filterControls = (
    <>
      <label className="sr-only" htmlFor="sandbox-component-library-search">
        Filter examples
      </label>
      <input
        ref={searchRef}
        id="sandbox-component-library-search"
        type="search"
        role="searchbox"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoComplete="off"
        placeholder="Search title, tags, or sample content…"
        aria-label="Filter examples by title, tag, description, or sample HTML text"
        className="w-full rounded-lg border border-outline-variant/35 bg-surface-container-lowest px-3 py-2 font-mono text-xs text-on-surface placeholder:text-on-surface-variant/60 outline-none focus-visible:border-primary-container focus-visible:ring-2 focus-visible:ring-primary-container/30"
      />
      {!embeddedInDrawer ? (
        <div className="min-w-0">
          <div className="mb-2 hidden md:block">
            <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">
              Tags
            </p>
            <p className="mt-1 font-mono text-[9px] leading-snug text-on-surface-variant/90">
              Toggle tags. Examples stay visible if they include any selected tag.
            </p>
          </div>
          <button
            type="button"
            id="sandbox-library-tags-disclosure"
            aria-expanded={tagsExpanded}
            aria-controls="sandbox-library-tags-panel"
            onClick={() => setTagsExpanded((v) => !v)}
            className="mb-2 flex w-full items-center justify-between gap-2 rounded-lg border border-outline-variant/35 bg-surface-container-lowest px-3 py-2 text-left font-mono outline-none transition-colors hover:bg-surface-container-high/40 focus-visible:border-primary-container focus-visible:ring-2 focus-visible:ring-primary-container/30 md:hidden"
          >
            <span className="min-w-0">
              <span className="block text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">
                Tags
              </span>
              <span className="mt-0.5 block text-[9px] leading-snug text-on-surface-variant/85">
                {activeTags.length === 0
                  ? 'Tap to filter by topic'
                  : `${activeTags.length} selected — tap to change`}
              </span>
            </span>
            <span
              className="material-symbols-outlined shrink-0 text-on-surface-variant"
              aria-hidden
            >
              {tagsExpanded ? 'expand_less' : 'expand_more'}
            </span>
          </button>
          <div
            id="sandbox-library-tags-panel"
            className={`max-h-48 flex-wrap gap-1.5 overflow-y-auto overscroll-contain pr-0.5 md:flex md:max-h-[min(40vh,18rem)] ${
              tagsExpanded ? 'flex' : 'hidden md:flex'
            }`}
          >
            {allTags.map((tag) => {
              const selected = activeTags.includes(tag)
              return (
                <button
                  key={tag}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => toggleTag(tag)}
                  className={`${TAG_CHIP_CLASS} border-outline-variant/30 ${
                    selected
                      ? 'border-primary-container/60 bg-primary-container/20 text-primary-container'
                      : 'bg-surface-container-high/60 text-on-surface-variant hover:border-outline-variant hover:bg-surface-container-high'
                  }`}
                >
                  {tag}
                </button>
              )
            })}
          </div>
        </div>
      ) : null}
      {!embeddedInDrawer && (query.trim() !== '' || activeTags.length > 0) ? (
        <button
          type="button"
          onClick={clearFilters}
          className="font-mono text-[10px] font-bold uppercase tracking-widest text-primary-container underline decoration-primary-container/35 underline-offset-2 hover:decoration-primary-container"
        >
          Clear filters
        </button>
      ) : null}
    </>
  )

  if (embeddedInDrawer) {
    return (
      <section
        aria-labelledby="sandbox-component-library-heading"
        className="flex h-full min-h-0 flex-1 flex-col overflow-hidden"
      >
        <div className="shrink-0 space-y-3 border-b border-outline-variant/10 bg-surface-container-low px-4 py-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2
                id="sandbox-component-library-heading"
                className="font-headline text-xs font-bold tracking-widest text-on-surface-variant uppercase"
              >
                Component library
              </h2>
              <p className="mt-1 font-mono text-[10px] leading-snug text-on-surface-variant">
                Search matches example title, tags, description, category, preset id, and text inside the
                sample HTML.
              </p>
            </div>
            <Link
              href="/tools/sandbox/library"
              className="shrink-0 font-mono text-[10px] font-bold tracking-widest text-primary-container uppercase underline decoration-primary-container/35 underline-offset-2 hover:decoration-primary-container"
            >
              Full library →
            </Link>
          </div>
          {filterControls}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <GridList
            filtered={filtered}
            variant={variant}
            copyFlash={copyFlash}
            setCopyFlash={setCopyFlash}
            onLoad={onLoad}
          />
        </div>
      </section>
    )
  }

  return (
    <section
      aria-labelledby="sandbox-component-library-heading"
      className="flex w-full min-w-0 flex-col overflow-visible rounded-lg border border-outline-variant/20 bg-surface-container-low"
    >
      <div className="flex flex-col md:flex-row md:items-start">
        <div className="sticky top-28 z-30 w-full shrink-0 self-start md:top-20 md:w-56">
          <aside className="flex max-h-[min(40vh,calc(100dvh-7.5rem))] flex-col gap-3 overflow-y-auto overscroll-contain border-b border-outline-variant/15 bg-surface-container-low p-3 md:max-h-[calc(100dvh-5rem)] md:border-r md:border-b-0 md:py-4">
            <div>
              <h2
                id="sandbox-component-library-heading"
                className="font-headline text-xs font-bold tracking-widest text-on-surface-variant uppercase"
              >
                Filter
              </h2>
              <p className="mt-1 font-mono text-[10px] leading-snug text-on-surface-variant">
                Search across title, tags, description, category, id, and visible sample text. Use tags to narrow
                by topic.
              </p>
            </div>
            {filterControls}
          </aside>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex shrink-0 items-center border-b border-outline-variant/10 bg-surface-container-low px-3 py-2">
            <span className="font-mono text-[10px] tracking-wide text-on-surface-variant">
              <span className="font-bold text-on-surface">{filtered.length}</span>
              {filtered.length === 1 ? ' example' : ' examples'}
            </span>
          </div>
          <GridList
            filtered={filtered}
            variant={variant}
            copyFlash={copyFlash}
            setCopyFlash={setCopyFlash}
            onLoad={onLoad}
          />
        </div>
      </div>
    </section>
  )
}
